import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2 } from "lucide-react";
import {
    WorkspaceTabs,
    DecisionTree,
    Checklist,
    PenaltyCalculator,
    WorkspaceHero,
} from "../components/workspace/Primitives";

const TABS = [
    { key: "classifier", label: "Risk classifier" },
    { key: "obligations", label: "Obligation explorer" },
    { key: "timeline", label: "Timeline" },
    { key: "penalties", label: "Penalty calculator" },
    { key: "checklist", label: "Conformity checklist" },
];

export default function AiActWorkspace() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("classifier");
    const [obligationRole, setObligationRole] = useState("provider_high_risk");

    useEffect(() => {
        api.get("/ai-act").then(({ data }) => setData(data));
    }, []);

    if (!data) {
        return (
            <div className="container-edit py-14 flex items-center gap-3 text-[#0D1B3E]/60">
                <Loader2 className="animate-spin" size={18} />
                <span className="label-eyebrow">Loading AI Act workspace…</span>
            </div>
        );
    }

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="ai-act-workspace">
            <WorkspaceHero
                eyebrow="Workspace · Regulation (EU) 2024/1689"
                title="AI Act"
                subtitle="Classify a use case, explore obligations by role, see what applies when, model worst-case fines, and track conformity readiness."
                badges={["In force since 2024-08-01", "Main body from 2026-08-02", "Omnibus (pending): Annex III 2027-12-02 · Annex I 2028-08-02"]}
            />

            <div className="mt-8" data-testid="ai-act-tabs-wrap">
                <WorkspaceTabs
                    tabs={TABS}
                    active={tab}
                    onChange={setTab}
                    testIdPrefix="ai-act"
                />
            </div>

            <div className="mt-9">
                {tab === "classifier" && (
                    <section className="max-w-3xl" data-testid="ai-act-classifier">
                        <DecisionTree tree={data.risk_classifier} testIdPrefix="ai-act-classifier" />
                    </section>
                )}

                {tab === "obligations" && (
                    <section data-testid="ai-act-obligations">
                        <div className="flex flex-wrap gap-2 mb-6">
                            {Object.entries(data.obligations).map(([k, v]) => (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={() => setObligationRole(k)}
                                    className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                                        obligationRole === k
                                            ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                            : "bg-transparent text-[#0D1B3E]/75 border-[#0D1B3E]/25 hover:border-[#0D1B3E]"
                                    }`}
                                    data-testid={`ai-act-role-${k}`}
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>
                        <div className="card-flat p-6">
                            <div className="label-eyebrow">
                                Applies from {data.obligations[obligationRole].applies_from}
                            </div>
                            <h3 className="mt-1 font-display text-2xl tracking-tight">
                                {data.obligations[obligationRole].label}
                            </h3>
                        </div>
                        <ul className="mt-5 space-y-3" data-testid="ai-act-obligation-list">
                            {data.obligations[obligationRole].items.map((it, i) => (
                                <li key={i} className="card-flat p-5">
                                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                        {it.article}
                                    </div>
                                    <div className="mt-1 font-display text-lg tracking-tight">
                                        {it.title}
                                    </div>
                                    <p className="mt-2 text-sm text-[#0D1B3E]/80 leading-relaxed">
                                        {it.body}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {tab === "timeline" && (
                    <section data-testid="ai-act-timeline-section">
                        <ol className="relative border-l-2 border-[#C9A84C]/40 ml-3 space-y-9">
                            {data.timeline.map((w) => (
                                <li
                                    key={w.wave}
                                    className="pl-7 relative"
                                    data-testid={`ai-act-wave-${w.wave}`}
                                >
                                    <span
                                        className={`absolute -left-[11px] top-1 w-5 h-5 rounded-full border-4 border-[#F5F0E8] ${
                                            w.status === "in_force"
                                                ? "bg-[#C9A84C]"
                                                : "bg-[#0D1B3E]"
                                        }`}
                                    />
                                    <div className="flex items-center gap-3 flex-wrap label-eyebrow">
                                        <span style={{ color: "#C9A84C" }}>{w.date}</span>
                                        <span>· {w.article}</span>
                                        <span className="px-2 py-0.5 border border-[#0D1B3E]/20">
                                            {w.status === "in_force" ? "In force" : "Upcoming"}
                                        </span>
                                    </div>
                                    <div className="font-display text-2xl tracking-tight mt-2">
                                        Wave {w.wave}: {w.title}
                                    </div>
                                    <p className="mt-2 text-sm text-[#0D1B3E]/80 leading-relaxed">
                                        {w.body}
                                    </p>
                                </li>
                            ))}
                        </ol>
                    </section>
                )}

                {tab === "penalties" && (
                    <section data-testid="ai-act-penalties">
                        <PenaltyCalculator
                            bands={data.penalties}
                            testIdPrefix="ai-act"
                        />
                        <p className="mt-5 text-xs text-[#0D1B3E]/60">
                            Article 99 ceilings. Member-State enforcement may impose additional sanctions.
                        </p>
                    </section>
                )}

                {tab === "checklist" && (
                    <section data-testid="ai-act-checklist-section">
                        <Checklist
                            groups={data.conformity_checklist.groups}
                            storageKey="ai_act_conformity_v1"
                            testIdPrefix="ai-act"
                        />
                    </section>
                )}
            </div>
        </div>
    );
}
